// Polymorphic parent reference cell. Renders the variation's full target
// list:
//   • A `kind: 'contract'` target shows as italic muted "Contract" (no
//     link — self-reference).
//   • A `kind: 'sow'` target shows as a brand-coloured button with the
//     resolved SOW number + ExternalLink icon. Click fires
//     `onNavigate(sowId)` for the parent to scroll-and-expand.
//   • Multiple targets are joined by middle-dot bullets.
//   • When the list exceeds the truncation cap (currently 2), trailing
//     entries collapse into a muted "+N more" suffix; the full list is
//     surfaced via the cell's `title` tooltip so the audit row stays
//     inspectable. SOW links remain individually clickable for the
//     visible entries.
//
// SOW name resolution lives upstream — the parent table passes
// `sowsById` so each cell can look up the SOW's number without spawning
// per-row fetches. An unresolved id (archived / deleted SOW) renders the
// stored sowId in a muted slate fallback.

import { Fragment } from "react";
import { ExternalLink } from "lucide-react";
import type { Sow, VariationTarget } from "../../../../types";

// Truncation threshold: a Contract entry plus 2 SOW labels still fits in
// the 18% column width at typical SOW number widths. Anything beyond that
// folds into a "+N more" suffix with the full list available via title.
const TRUNCATE_AFTER = 3;

export interface VariesCellProps {
  /** The variation's target list. Empty list renders an em-dash as a
   *  defensive fallback — the form requires ≥1 target on submit so this
   *  should not occur for stored records. */
  targets: VariationTarget[];
  /** Parent-supplied lookup map. Keys are SOW ids, values the resolved
   *  SOW row. May be empty during initial load. */
  sowsById: Map<string, Sow>;
  /** Imperative navigation handler — parent scrolls to and expands the
   *  referenced SOW row. Only fired for `kind: 'sow'` targets. */
  onNavigate?: (sowId: string) => void;
  testId?: string;
}

function targetLabel(t: VariationTarget, sowsById: Map<string, Sow>): string {
  if (t.kind === "contract") return "Contract";
  const sow = sowsById.get(t.sowId);
  return sow ? sow.number : t.sowId;
}

interface TargetTokenProps {
  target: VariationTarget;
  sowsById: Map<string, Sow>;
  onNavigate?: (sowId: string) => void;
}

function TargetToken({ target, sowsById, onNavigate }: TargetTokenProps) {
  if (target.kind === "contract") {
    return (
      <span className="italic text-slate-500 dark:text-slate-400">
        Contract
      </span>
    );
  }
  const sow = sowsById.get(target.sowId);
  if (!sow) {
    return (
      <span
        className="font-mono text-slate-400 dark:text-slate-500"
        title="Referenced SOW could not be resolved"
      >
        {target.sowId}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onNavigate?.(sow.id)}
      aria-label={`Navigate to SOW ${sow.number}`}
      className="inline-flex items-center gap-1 font-medium text-brand-600 dark:text-brand-400 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm"
    >
      <span className="font-mono">{sow.number}</span>
      <ExternalLink className="w-3 h-3" aria-hidden="true" />
    </button>
  );
}

export default function VariesCell({
  targets,
  sowsById,
  onNavigate,
  testId,
}: VariesCellProps) {
  if (targets.length === 0) {
    return (
      <span
        data-testid={testId}
        className="text-body-sm text-slate-400 dark:text-slate-500"
      >
        —
      </span>
    );
  }

  const truncated = targets.length > TRUNCATE_AFTER;
  const visible = truncated ? targets.slice(0, TRUNCATE_AFTER) : targets;
  const hidden = truncated ? targets.length - TRUNCATE_AFTER : 0;

  // Full list always populates the title tooltip. Even when not
  // truncated, hovering surfaces the full label set in one consistent
  // string — useful for the audit row to expose every target in one
  // hover regardless of column width.
  const fullText = targets.map((t) => targetLabel(t, sowsById)).join(" · ");

  return (
    <span
      data-testid={testId}
      title={fullText}
      className={`text-body-sm ${
        targets.length === 1 && targets[0].kind === "contract"
          ? "italic text-slate-500 dark:text-slate-400"
          : "text-slate-700 dark:text-slate-200"
      }`}
    >
      {visible.map((t, i) => (
        <Fragment
          key={t.kind === "contract" ? "contract" : `sow-${t.sowId}-${i}`}
        >
          {i > 0 && (
            <span
              aria-hidden="true"
              className="mx-1 text-slate-400 dark:text-slate-500"
            >
              ·
            </span>
          )}
          {targets.length === 1 && t.kind === "contract" ? (
            // Single contract target: render the bare label (no nested
            // italic span) so the cell-level italic styling applies once.
            "Contract"
          ) : (
            <TargetToken
              target={t}
              sowsById={sowsById}
              onNavigate={onNavigate}
            />
          )}
        </Fragment>
      ))}
      {truncated && (
        <>
          <span
            aria-hidden="true"
            className="mx-1 text-slate-400 dark:text-slate-500"
          >
            ·
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            +{hidden} more
          </span>
        </>
      )}
    </span>
  );
}
